'use client';

import React from 'react';
import Link from 'next/link';
import { Star, MapPin, BadgeCheck, Zap, Crown, Clock, ChevronLeft, ChevronRight, MessageSquare } from 'lucide-react';
import AppImage from '@/components/ui/AppImage';
import type { Provider } from './types';

interface Props {
  providers: Provider[];
  total: number;
  page: number;
  totalPages: number;
  perPage: number;
  setPage: (p: number) => void;
  viewMode: 'grid' | 'map';
}

const TierBadge = ({ tier }: { tier: Provider['tier'] }) => {
  if (tier === 'premium') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold tier-premium">
      <Crown size={9} />Premium
    </span>
  );
  if (tier === 'pro') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold tier-pro">
      <Zap size={9} />Pro
    </span>
  );
  return null;
};

function ProviderCard({ provider }: { provider: Provider }) {
  return (
    <Link
      href="/public-provider-profile"
      className="group block bg-white border border-gray-100 rounded-2xl overflow-hidden hover:border-plum-200 hover:shadow-card-hover transition-all duration-200"
    >
      {/* Tier accent bar */}
      {provider.tier === 'premium' && (
        <div className="h-1 bg-gradient-to-r from-amber-400 to-amber-500" />
      )}
      {provider.tier === 'pro' && (
        <div className="h-1 bg-gradient-to-r from-plum-400 to-plum-600" />
      )}

      <div className="p-5">
        {/* Header row */}
        <div className="flex items-start gap-3 mb-3">
          <div className="relative flex-shrink-0">
            <AppImage
              src={provider.avatar}
              alt={provider.avatarAlt}
              width={56}
              height={56}
              className="w-14 h-14 rounded-xl object-cover"
            />
            {provider.verified && (
              <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-mint-500 rounded-full flex items-center justify-center ring-2 ring-white">
                <BadgeCheck size={11} className="text-white" />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-1 mb-0.5">
              <h3 className="font-display font-700 text-gray-900 text-sm truncate pr-1">{provider.name}</h3>
              <TierBadge tier={provider.tier} />
            </div>
            <p className="text-gray-500 text-xs mb-1">{provider.trade}</p>
            <div className="flex items-center gap-1 text-gray-400 text-xs">
              <MapPin size={11} />
              <span>{provider.location}</span>
              <span className="text-gray-300">·</span>
              <span className="tabular-nums">{provider.distanceMiles} mi</span>
            </div>
          </div>
        </div>

        {/* Tagline */}
        <p className="text-gray-600 text-sm leading-relaxed line-clamp-2 mb-3">{provider.tagline}</p>

        {/* Specialisms */}
        <div className="flex flex-wrap gap-1.5 mb-4">
          {provider.specialisms.slice(0, 3).map((spec) => (
            <span
              key={`${provider.id}-spec-${spec}`}
              className="px-2 py-0.5 bg-gray-50 border border-gray-100 rounded-full text-xs text-gray-600"
            >
              {spec}
            </span>
          ))}
          {provider.specialisms.length > 3 && (
            <span className="px-2 py-0.5 bg-gray-50 border border-gray-100 rounded-full text-xs text-gray-400">
              +{provider.specialisms.length - 3}
            </span>
          )}
        </div>

        {/* Stats row */}
        <div className="flex items-center justify-between pt-3 border-t border-gray-50">
          <div className="flex items-center gap-1">
            <Star size={13} className="text-amber-400 fill-amber-400" />
            <span className="font-semibold text-sm text-gray-800 tabular-nums">{provider.rating}</span>
            <span className="text-gray-400 text-xs">({provider.reviewCount})</span>
          </div>
          <div className="flex items-center gap-3">
            <div className={`flex items-center gap-1 text-xs ${provider.availableNow ? 'text-mint-600' : 'text-gray-400'}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${provider.availableNow ? 'bg-mint-500' : 'bg-gray-300'}`} />
              {provider.availableNow ? 'Available' : 'Busy'}
            </div>
            <span className="flex items-center gap-1 text-xs text-gray-400">
              <Clock size={11} />
              {provider.responseTime}
            </span>
          </div>
        </div>

        {/* CTA — appears on hover */}
        <div className="mt-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          {provider.tier === 'premium' ? (
            <button className="w-full py-2 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 text-white text-xs font-semibold flex items-center justify-center gap-1.5 hover:from-amber-600 hover:to-amber-700 transition-all active:scale-95">
              Book a Call
            </button>
          ) : provider.tier === 'pro' ? (
            <button className="w-full py-2 rounded-xl bg-plum-800 text-white text-xs font-semibold flex items-center justify-center gap-1.5 hover:bg-plum-900 transition-all active:scale-95">
              <Zap size={12} />
              Instant Quote
            </button>
          ) : (
            <button className="w-full py-2 rounded-xl border border-gray-200 text-gray-700 text-xs font-semibold flex items-center justify-center gap-1.5 hover:bg-gray-50 transition-all active:scale-95">
              <MessageSquare size={12} />
              Request Quote
            </button>
          )}
        </div>
      </div>
    </Link>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mb-4">
        <MapPin size={28} className="text-gray-300" />
      </div>
      <h3 className="font-display font-700 text-gray-800 text-lg mb-2">No providers found</h3>
      <p className="text-gray-500 text-sm max-w-xs">
        Try adjusting your postcode, radius, or trade category to find providers near you.
      </p>
    </div>
  );
}

export default function ProviderGrid({
  providers, total, page, totalPages, perPage, setPage, viewMode
}: Props) {
  if (viewMode === 'map') {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden" style={{ height: '600px' }}>
        <div className="w-full h-full bg-gradient-to-br from-plum-50 to-mint-50 flex flex-col items-center justify-center gap-3">
          <MapPin size={36} className="text-plum-300" />
          <p className="font-display font-600 text-gray-600">Map view</p>
          <p className="text-gray-400 text-sm text-center max-w-xs">
            {/* Backend integration point: initialize Mapbox GL JS here with provider lat/lng pins */}
            Mapbox integration renders {providers.length} provider pins here. Connect NEXT_PUBLIC_MAPBOX_TOKEN.
          </p>
        </div>
      </div>
    );
  }

  if (providers.length === 0) return <EmptyState />;

  const startItem = (page - 1) * perPage + 1;
  const endItem = Math.min(page * perPage, total);

  return (
    <div>
      {/* Result count */}
      <p className="text-sm text-gray-500 mb-4">
        Showing <span className="font-semibold text-gray-800 tabular-nums">{startItem}–{endItem}</span> of{' '}
        <span className="font-semibold text-gray-800 tabular-nums">{total}</span> providers
      </p>

      {/* Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-3 gap-4 xl:gap-5 mb-6">
        {providers.map((provider) => (
          <ProviderCard key={provider.id} provider={provider} />
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
          <button
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page === 1}
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all active:scale-95"
          >
            <ChevronLeft size={15} />
            Previous
          </button>

          <div className="flex items-center gap-1">
            {Array.from({ length: Math.min(totalPages, 7) }).map((_, i) => {
              const pageNum = i + 1;
              return (
                <button
                  key={`page-btn-${pageNum}`}
                  onClick={() => setPage(pageNum)}
                  className={`w-8 h-8 rounded-lg text-sm font-medium transition-all ${
                    page === pageNum
                      ? 'bg-plum-800 text-white' :'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>

          <button
            onClick={() => setPage(Math.min(totalPages, page + 1))}
            disabled={page === totalPages}
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all active:scale-95"
          >
            Next
            <ChevronRight size={15} />
          </button>
        </div>
      )}
    </div>
  );
}